/**
 * /tenants/$slug/posture/compliance/frameworks/$frameworkId/$frameworkVersion — route shim.
 *
 * Validates URL search params via Zod (filter facets), prefetches
 * the framework detail via ensureQueryData, then mounts the page.
 */

import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { fetchFrameworkDetail } from "@/features/compliance/queries";
import { FrameworkDetailPage } from "@/features/compliance/pages/framework-detail-page";

const searchSchema = z.object({
  // .catch(undefined) so a malformed URL filter degrades to "show
  // all" instead of throwing the whole page into an error boundary.
  q: z.string().optional().catch(undefined),
  status: z
    .enum([
      "all",
      "findings",
      "compliant",
      "partial",
      "non_compliant",
      "not_applicable",
      "not_assessed",
    ])
    .optional()
    .catch(undefined),
  severity: z
    .enum(["all", "critical", "high", "medium", "low"])
    .optional()
    .catch(undefined),
  family: z.string().optional().catch(undefined),
  // The register's own filters and sort. In the address bar because an
  // operator who opens a control, reads it and comes back must land where
  // they were — and "the three not-met rows I am looking at" should be a
  // link they can send to whoever owns them.
  know: z.enum(["all", "auto", "structural", "manual", "unchecked"]).optional().catch(undefined),
  met: z.enum(["all", "not_met", "partly"]).optional().catch(undefined),
  // Attention-first is the default and therefore the value NOT in the
  // URL: a default that has to be spelled out is one every link carries.
  sort: z.enum(["attention", "instrument"]).optional().catch(undefined),
  // The facet a finding links into: the attention reason that produced
  // it. One mechanism rather than one param per finding, so a finding's
  // stated count and the rows its link shows are the same set by
  // construction — they were computed by different predicates before, and
  // "12 controls have no owner" landed on a register reading "18 of 40".
  reason: z
    .enum([
      "exception_lapsed",
      "not_met",
      "partly_met",
      "expiring",
      "unchecked",
      "unowned",
    ])
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute(
  "/_authed/tenants/$slug/posture/compliance/frameworks/$frameworkId/$frameworkVersion",
)({
  validateSearch: (raw: Record<string, unknown>) => searchSchema.parse(raw),
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData({
      queryKey: [
        "compliance-v2",
        "framework",
        context.tenant.id,
        params.frameworkId,
        params.frameworkVersion,
      ],
      queryFn: () =>
        fetchFrameworkDetail(
          context.tenant.id,
          params.frameworkId,
          params.frameworkVersion,
        ),
    }),
  component: FrameworkDetailPage,
});
